import { useState, useEffect, useRef } from 'react';
import { Bell, Heart, UserPlus, Newspaper, Trophy, CheckCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

const ICON_MAP = {
  like: Heart,
  follow: UserPlus,
  leaderboard: Trophy,
  rss: Newspaper,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifs = async () => {
    try {
      const { data } = await api.get('/notifications?limit=20');
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) {
      api.put(`/notifications/${notif.notification_id}/read`).catch(() => {});
      setNotifs(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    }
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted/50 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} weight={unread > 0 ? 'fill' : 'regular'} className="text-foreground" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto bg-card rounded-lg shadow-xl z-50" style={{ border: '1px solid hsl(var(--border))' }} data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <h3 className="text-sm font-sans font-bold text-foreground">Notifications</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-primary font-sans font-semibold uppercase tracking-wider hover:underline" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          {notifs.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell size={24} className="mx-auto text-muted-foreground/30" />
              <p className="text-xs text-muted-foreground font-sans mt-2">No notifications yet</p>
            </div>
          ) : (
            <div>
              {notifs.map(n => {
                const Icon = ICON_MAP[n.type] || Bell;
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => handleClick(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors ${!n.read ? 'bg-primary/3' : ''}`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                      n.type === 'like' ? 'bg-red-500/10 text-red-500' :
                      n.type === 'follow' ? 'bg-blue-500/10 text-blue-500' :
                      n.type === 'leaderboard' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      <Icon size={14} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-sans leading-snug ${!n.read ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                        {n.message}
                      </p>
                      <p className="text-[9px] text-muted-foreground font-sans mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
